import { Link } from "@/lib/router-compat";
import logo from "@/assets/logo.png";
import { Phone, Mail, MapPin, ArrowRight, BadgeCheck, CalendarCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PHONE_DISPLAY, PHONE_TEL_HREF, SERVICE_AREA_SHORT } from "@/config/business";

const Footer = () => {
  return (
    <footer className="bg-offwhite text-foreground border-t border-border" role="contentinfo">
      {/* Pre-footer CTA band */}
      <div className="border-b border-border bg-primary/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <p className="text-primary font-semibold text-xs uppercase tracking-widest mb-2">Free Virtual Consultation</p>
            <h3 className="text-2xl sm:text-3xl font-bold leading-tight text-foreground">
              Ready for a fence that protects without the eyesore?
            </h3>
            <p className="text-muted-foreground mt-2 text-sm sm:text-base">
              Timelines are confirmed in your written proposal. No pressure, no door-to-door sales.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
            <Button variant="cta" size="lg" className="px-8 py-6 gap-2" asChild>
              <Link to="/book">Book Free Consultation <ArrowRight className="w-5 h-5" /></Link>
            </Button>
            <Button size="lg" variant="outline" className="px-8 py-6" asChild>
              <Link to="/beat-my-estimate">Beat My Estimate</Link>
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand */}
          <div>
            <Link to="/">
              <img src={logo} alt="Modern Fence Install LLC logo" width={350} height={116} className="w-auto h-16 sm:h-20 lg:h-[100px] mb-4" />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed max-w-xs">
              Professional non-electric containment fence installation for homeowners and property owners. Safe, durable, and built to last.
            </p>
          </div>

          {/* Services */}
          <nav aria-label="Services navigation">
            <h4 className="font-semibold text-sm uppercase tracking-widest mb-4 text-foreground">Services</h4>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li><Link to="/services/dog-fence" className="hover:text-foreground transition-colors">Dog Fence Installation</Link></li>
              <li><Link to="/services/deer-fence" className="hover:text-foreground transition-colors">Deer Fence Installation</Link></li>
              <li><Link to="/services/garden-fence" className="hover:text-foreground transition-colors">Garden Fence Installation</Link></li>
              <li><Link to="/services/fence-restoration" className="hover:text-foreground transition-colors">Fence Restoration</Link></li>
              <li><Link to="/fence-tuneup" className="hover:text-foreground transition-colors">Fence Tune-Up ($299)</Link></li>
              <li><Link to="/financing" className="hover:text-foreground transition-colors">Affirm financing</Link></li>
            </ul>
          </nav>

          {/* Quick links */}
          <nav aria-label="Footer navigation">
            <h4 className="font-semibold text-sm uppercase tracking-widest mb-4 text-foreground">Company</h4>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li><Link to="/about" className="hover:text-foreground transition-colors">About Us</Link></li>
              <li><Link to="/gallery" className="hover:text-foreground transition-colors">Project Gallery</Link></li>
              <li><Link to="/neighbors" className="hover:text-foreground transition-colors">Neighbor Map</Link></li>
              <li><Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link></li>
              <li><Link to="/book" className="hover:text-foreground transition-colors">Book a Consultation</Link></li>
              <li><Link to="/share-your-fence" className="hover:text-foreground transition-colors">Share Your Fence — Get $100</Link></li>
              <li><Link to="/refer" className="hover:text-foreground transition-colors">Refer & Earn $100</Link></li>
              <li><Link to="/franchise-opportunities" className="hover:text-foreground transition-colors">Franchise Opportunities</Link></li>
              <li><Link to="/account" className="hover:text-foreground transition-colors">My Account</Link></li>
              <li><Link to="/terms" className="hover:text-foreground transition-colors">Terms of Service</Link></li>
              <li><Link to="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link></li>
              <li><Link to="/cookies" className="hover:text-foreground transition-colors">Cookie Policy</Link></li>
            </ul>
          </nav>

          {/* Service Areas */}
          <nav aria-label="Service areas navigation">
            <h4 className="font-semibold text-sm uppercase tracking-widest mb-4 text-foreground">Service Areas</h4>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li><Link to="/locations/fairfield-county-ct" className="hover:text-foreground transition-colors">Fairfield County, CT</Link></li>
              <li><Link to="/locations/hartford-county-ct" className="hover:text-foreground transition-colors">Hartford County, CT</Link></li>
              <li><Link to="/locations/westchester-ny" className="hover:text-foreground transition-colors">Westchester County, NY</Link></li>
              <li><Link to="/locations/metrowest-ma" className="hover:text-foreground transition-colors">MetroWest, MA</Link></li>
              <li><Link to="/locations/newport-county-ri" className="hover:text-foreground transition-colors">Newport County, RI</Link></li>
              <li><Link to="/locations/southern-vermont" className="hover:text-foreground transition-colors">Southern Vermont</Link></li>
              <li><Link to="/locations" className="text-primary hover:underline font-medium">View all locations →</Link></li>
            </ul>
          </nav>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-widest mb-4 text-foreground">Get In Touch</h4>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li>
                <a href={PHONE_TEL_HREF} className="flex items-center gap-2 hover:text-foreground transition-colors">
                  <Phone className="w-4 h-4 text-primary" />
                  {PHONE_DISPLAY}
                </a>
              </li>
              <li>
                <a href="mailto:info@modernfenceinstall.com" className="flex items-center gap-2 hover:text-foreground transition-colors">
                  <Mail className="w-4 h-4 text-primary" />
                  info@modernfenceinstall.com
                </a>
              </li>
              <li className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
                {SERVICE_AREA_SHORT}
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="border-t border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 grid gap-4 md:grid-cols-3 items-center">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><BadgeCheck className="w-3.5 h-3.5 text-primary" />Licensed &amp; Insured</span>
            <span className="inline-flex items-center gap-1.5"><CalendarCheck className="w-3.5 h-3.5 text-primary" />Timeline Confirmed in Proposal</span>
          </div>
          <p className="text-xs text-muted-foreground text-center">
            &copy; {new Date().getFullYear()} Modern Fence Install LLC. All rights reserved.
          </p>
          <div className="text-xs text-muted-foreground text-center md:text-right space-y-1">
            <p className="italic">Minimum project size: 200 linear feet.</p>
            <div className="flex items-center justify-center md:justify-end gap-3">
              <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
              <span className="text-border">|</span>
              <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
              <span className="text-border">|</span>
              <Link to="/cookies" className="hover:text-foreground transition-colors">Cookies</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
